<template>
  <div class="animated fadeIn">
    <div class="container py-4">
      <!-- Header Card -->
      <div class="card bg-primary text-white mb-4">
        <h5 class="card-header mb-0">{{ $t('Webportal.Actions.InquiryWizard.General.Title') }}</h5>
      </div>

      <!-- Form Card -->
      <b-card class="mb-4" v-bind:class="{ spinner: isSubmitting }">
        <!-- Topic Selection -->
        <b-form-group label-class="font-weight-bold" class="mb-4">
          <template #label>
            {{ $t('Webportal.Actions.InquiryWizard.General.Topic') }}
            <span class="text-danger">*</span>
          </template>
          <b-form-select v-model="selectedTopic" :options="generalInquiryTopicOptions" />
        </b-form-group>

        <!-- Optional Check Selection -->
        <div class="form-group mb-4">
          <label for="check-select" class="font-weight-bold">
            {{ $t('Webportal.Actions.InquiryWizard.General.CheckReport') }}
            <span class="text-muted small">({{ $t('Webportal.Actions.InquiryWizard.DeadlineHint') }})</span>
          </label>
          <treeselect
            v-model="selectedCheckId"
            :async="true"
            :default-options="true"
            :load-options="loadCheckOptions"
            :clearable="true"
            :searchable="true"
            :disable-fuzzy-matching="true"
            :placeholder="checkPlaceholder"
            retry-text=""
            @open="checkPlaceholder = $t('Webportal.Actions.InquiryWizard.General.CheckSearchPlaceholder')"
            @close="checkPlaceholder = $t('Webportal.Actions.InquiryWizard.General.CheckNoSelection')"
            @input="onCheckSelected"
          />
        </div>

        <!-- Description Textarea -->
        <b-form-group label-class="font-weight-bold" class="mb-4">
          <template #label>
            {{ $t('Webportal.Actions.InquiryWizard.General.Description') }}
            <span class="text-danger">*</span>
          </template>
          <b-form-textarea v-model="description" rows="6" :placeholder="$t('Webportal.Actions.InquiryWizard.General.DescriptionPlaceholder')" />
          <div class="d-flex align-items-center justify-content-between mt-2">
            <div class="small">
              <span :class="{ 'text-danger': description.length < 20 }">
                {{ descriptionValidationMessage }}
              </span>
            </div>
            <div class="small text-muted">{{ $t('Webportal.Actions.InquiryWizard.CharCount', { count: descriptionCharCount }) }}</div>
          </div>
        </b-form-group>

        <!-- Contact Option -->
        <b-form-group v-if="isGeneralInquiryContactOptionEnabled" label-class="font-weight-bold" class="mb-4">
          <template #label> {{ $t('Webportal.Actions.InquiryWizard.General.ContactOption') }} </template>
          <b-form-input v-model="contactOption" :placeholder="$t('Webportal.Actions.InquiryWizard.General.ContactOptionPlaceholder')" />
        </b-form-group>
      </b-card>

      <!-- Info Card -->
      <b-card header-bg-variant="warning" class="mb-4">
        <template #header>
          <div class="d-flex align-items-center">
            <font-awesome-icon icon="lightbulb" class="mr-2" />
            <h6 class="mb-0 font-weight-bold">{{ $t('Webportal.Actions.InquiryWizard.General.TipTitle') }}</h6>
          </div>
        </template>
        <p class="mb-0">
          {{ $t('Webportal.Actions.InquiryWizard.General.TipDescription') }}
        </p>
      </b-card>

      <!-- Navigation Buttons -->
      <div class="d-flex justify-content-between">
        <b-button variant="outline-secondary" @click="goBack">
          <font-awesome-icon :icon="['fal', 'chevron-left']" class="mr-2" />
          {{ $t('Webportal.Actions.InquiryWizard.Landing.BackToSelection') }}
        </b-button>
        <b-button variant="primary" :disabled="!isFormValid || isSubmitting || !clientId" @click="submitInquiry">
          <font-awesome-icon :icon="isSubmitting ? ['fal', 'spinner'] : ['fal', 'paper-plane']" :spin="isSubmitting" class="mr-2" />
          {{ $t('Webportal.Actions.InquiryWizard.General.Submit') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import ActionService from "@/services/ActionService";
import AuthenticationService from "@/services/AuthenticationService";
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";
import { delay } from "lodash";

const CHECK_OPTIONS_LIMIT = 200;

export default {
  name: "GeneralInquiry",

  mixins: [SurveyRevisionMixin],

  data() {
    return {
      selectedTopic: null,
      description: "",
      contactOption: "",
      selectedCheckId: null,
      selectedCheck: null,
      isSubmitting: false,
    checkPlaceholder: this.$t('Webportal.Actions.InquiryWizard.General.CheckNoSelection'),
    };
  },

  computed: {
    descriptionCharCount() {
      return this.description.length;
    },

    isFormValid() {
      return this.selectedTopic !== null && this.description.length >= 20;
    },

    descriptionValidationMessage() {
      if (this.description.length >= 20) {
        return "";
      }
      return this.$t('Webportal.Actions.InquiryWizard.General.DescriptionMinLength');
    },

    clientId() {
      // Determine ClientId based on selected check or fallback to user's ClientId
      return this.selectedCheck?.ClientId ?? AuthenticationService.localUserProfile.ClientId;
    },
  },

  methods: {
    mapCheckToOption(check) {
      return {
        id: check.SurveyInstanceId,
        label: check.RevisionMaxDate
          ? `${check.ExternalSurveyInstanceId} - ${check.ClientLocationName} (${this.$options.filters.msm_date(check.RevisionMaxDate)})`
          : `${check.ExternalSurveyInstanceId} - ${check.ClientLocationName}`,
        check,
      };
    },

    onCheckSelected(value) {
      this.selectedCheck = value ? this._lastSearchResults?.find((c) => c.SurveyInstanceId === value) ?? null : null;
    },

    async loadCheckOptions({ action, searchQuery, callback }) {
      if (action === "ASYNC_SEARCH") {
        try {
          const isDefaultLoad = !searchQuery;
          const response = await ActionService.getSurveyInstancesForInquiry({
            onlyCanCreateSurveyRevision: isDefaultLoad ? true : undefined,
            searchText: isDefaultLoad ? undefined : searchQuery,
            pageSize: isDefaultLoad ? undefined : CHECK_OPTIONS_LIMIT + 1,
          });
          const results = response.data || [];
          this._lastSearchResults = results;

          if (results.length > CHECK_OPTIONS_LIMIT) {
            callback(
              this.$t("Webportal.ItemCountExceedsThreshold", {
                count: `>${results.length}`,
                threshold: CHECK_OPTIONS_LIMIT,
              })
            );
          } else {
            callback(null, [{ id: "", label: this.$t('Webportal.Actions.InquiryWizard.General.GeneralInquiryOption') }, ...results.map(this.mapCheckToOption)]);
          }
        } catch (error) {
          console.error("Error searching checks:", error);
          callback(error);
        }
      }
    },

    goBack() {
      this.$router.push({ name: "InquiryLanding" });
    },

    async submitInquiry() {
      try {
        const payload = {
          Type: this.SurveyRevisionType.General,
          ClientId: this.clientId,
          Topic: this.selectedTopic,
          Remarks: this.description,
          ContactOption: this.contactOption || null,
          StatusId: this.SurveyRevisionStatus.New,
          AssignedToRole: this.revisionInitialAssignedToRole,
        };

        if (this.selectedCheckId) {
          payload.SurveyInstanceId = this.selectedCheckId;
        }

        await ActionService.setSurveyRevision(payload);

        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.General.SubmitSuccess'), {
          autoHideDelay: 3000,
          variant: "success",
          noCloseButton: true,
        });

        delay(() => {
          this.$router.push({ name: "InquirySuccess" });
        }, 1000);
      } catch (error) {
        console.error("Error submitting inquiry:", error);
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.General.SubmitError'), {
          autoHideDelay: 10000,
          variant: "danger",
          noCloseButton: true,
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Minimal custom styles — most styling via Bootstrap utility classes
</style>
